import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  DEFAULT_PROJECT_GRID_VIEW,
  ProjectGridColumnDef,
  ProjectGridFilter,
  ProjectGridModel,
  ProjectGridRow,
  ProjectGridView,
} from '../models/project-grid.types';
import { CureSheetFailure, CureSheetSummary } from './cure-sheet.model';
import {
  CureSheetEntitlement,
  ENTITLEMENT_UNKNOWN,
  isEntitledToCureSheets,
} from './cure-sheet-entitlement';
import { CureSheetsService, classifyCureSheetError } from './cure-sheets.service';

/**
 * The cure-sheet viewer: the customer-trimmed sheet list, and the selected
 * sheet's PDF.
 *
 * Built on the shared grid deliberately — Devices, Drums, Customers and Users are
 * already one table with four datasets, and a fifth list with its own markup
 * would read as a different product on the same screen.
 *
 * VIEWING ONLY. Selecting a sheet renders it and does nothing else: no values are
 * copied into a project, no form is pre-filled, nothing is written back. Joel
 * cancelled auto-populate on 2026-09-04 and this component is where that would
 * otherwise creep back in.
 */
@Component({
  selector: 'app-cure-sheets',
  templateUrl: './cure-sheets.component.html',
  styleUrls: ['./cure-sheets.component.scss'],
})
export class CureSheetsComponent implements OnInit, OnDestroy {
  /** Closed until the SERVER says otherwise — see cure-sheet-entitlement.ts. */
  entitlement: CureSheetEntitlement = ENTITLEMENT_UNKNOWN;

  sheets: CureSheetSummary[] = [];
  isLoading = false;
  listFailure: CureSheetFailure = 'none';

  selectedSheet: CureSheetSummary | null = null;
  pdfUrl: SafeResourceUrl | null = null;
  isPdfLoading = false;
  pdfFailure: CureSheetFailure = 'none';

  /** The raw object URL, kept so it can be revoked — these are up to 20 MB. */
  private objectUrl: string | null = null;
  private destroyed$ = new Subject<void>();

  constructor(private service: CureSheetsService, private sanitizer: DomSanitizer) {}

  get isEntitled(): boolean {
    return isEntitledToCureSheets(this.entitlement);
  }

  /**
   * Entitled, loaded, nothing wrong, and genuinely nothing published.
   *
   * An empty sheets array is a SUCCESSFUL answer meaning this customer has
   * exposed nothing yet. It is not an error, not a spinner and not a failure
   * toast — so it gets its own state with guidance, distinct from the
   * not-entitled one.
   */
  get isEmpty(): boolean {
    return this.isEntitled && !this.isLoading && this.listFailure === 'none' && this.sheets.length === 0;
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.releaseObjectUrl();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  load(): void {
    this.isLoading = true;
    this.listFailure = 'none';

    this.service
      .loadEffective()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.entitlement = result.entitlement;
          this.sheets = result.sheets;
          this.isLoading = false;
          this.buildGrid();
        },
        error: (error) => {
          this.isLoading = false;
          this.listFailure = classifyCureSheetError(error);
          this.sheets = [];
          this.buildGrid();
        },
      });
  }

  // ── The shared grid ────────────────────────────────────────────────────────
  readonly gridColumns: ProjectGridColumnDef[] = [
    // brandName, NOT fileName: the filename is the document, not the product.
    { id: 'brandName', label: 'Name', type: 'text', width: 300 },
    { id: 'version', label: 'Version', type: 'number', width: 110 },
    { id: 'description', label: 'Description', type: 'text', width: 320 },
    { id: 'updatedAt', label: 'Updated', type: 'date', width: 170 },
  ];

  gridModel: ProjectGridModel<CureSheetSummary> = {
    columns: [],
    rows: [],
    counts: { total: 0, live: 0, historical: 0, filtered: 0 },
  };

  gridView: ProjectGridView = { ...DEFAULT_PROJECT_GRID_VIEW, filters: {} };

  private gridSortColumn = 'brandName';
  private sortAsc = true;

  get selectedKey(): string | null {
    return this.selectedSheet ? this.selectedSheet.id : null;
  }

  /** The label for a sheet. Falls back to the id only so a row is never blank. */
  displayName(sheet: CureSheetSummary): string {
    return sheet.brandName?.trim() || sheet.id;
  }

  private cellText(sheet: CureSheetSummary, columnId: string): string {
    switch (columnId) {
      case 'brandName': return this.displayName(sheet);
      case 'version': return sheet.version == null ? '' : String(sheet.version);
      case 'description': return sheet.description ?? '';
      // Absent renders EMPTY — never a dash, never a fabricated date.
      case 'updatedAt': return sheet.updatedAt ?? '';
      default: return '';
    }
  }

  private buildGrid(): void {
    const visible = this.sheets.filter((s) => this.passesGridFilters(s));

    // Sorted by NAME by default, never by recency. The customer pinned the
    // version they want; ordering that implies newest = correct would quietly
    // argue with that choice.
    const sorted = [...visible].sort((a, b) => {
      const cmp = this.gridSortColumn === 'version'
        ? (a.version ?? 0) - (b.version ?? 0)
        : this.cellText(a, this.gridSortColumn).localeCompare(this.cellText(b, this.gridSortColumn));
      return this.sortAsc ? cmp : -cmp;
    });

    const rows: ProjectGridRow<CureSheetSummary>[] = sorted.map((sheet) => ({
      kind: 'project' as const,
      data: sheet,
      key: sheet.id,
      cells: this.gridColumns.map((c) => this.cellText(sheet, c.id)),
      standIn: this.gridColumns.map(() => false),
    }));

    this.gridModel = {
      columns: this.gridColumns,
      rows,
      counts: { total: this.sheets.length, live: 0, historical: 0, filtered: sorted.length },
    };
  }

  onGridSort(event: { columnId: string }): void {
    if (this.gridSortColumn === event.columnId) this.sortAsc = !this.sortAsc;
    else { this.gridSortColumn = event.columnId; this.sortAsc = true; }
    this.buildGrid();
  }

  onGridFilter(event: { columnId: string; filter: ProjectGridFilter | null }): void {
    const filters = { ...(this.gridView.filters ?? {}) };
    if (event.filter) filters[event.columnId] = event.filter;
    else delete filters[event.columnId];
    this.gridView = { ...this.gridView, filters };
    this.buildGrid();
  }

  private passesGridFilters(sheet: CureSheetSummary): boolean {
    for (const [columnId, filter] of Object.entries(this.gridView.filters ?? {})) {
      const text = this.cellText(sheet, columnId);
      if (filter.op === 'in') {
        const allowed = filter.values ?? [];
        if (allowed.length > 0 && !allowed.includes(text)) return false;
      } else if (filter.op === 'contains') {
        if (!text.toLowerCase().includes((filter.value ?? '').toLowerCase())) return false;
      } else if (filter.op === 'equals') {
        if (text.toLowerCase() !== (filter.value ?? '').toLowerCase()) return false;
      }
    }
    return true;
  }

  /** Select a sheet and render it. Renders; does not populate anything. */
  selectSheet(sheet: CureSheetSummary): void {
    if (!sheet) return;
    this.selectedSheet = sheet;
    this.pdfFailure = 'none';
    this.releaseObjectUrl();
    this.isPdfLoading = true;

    this.service
      .fetchPdf(sheet.id, sheet.version)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blob) => {
          this.isPdfLoading = false;
          this.objectUrl = URL.createObjectURL(blob);
          // Bypassed because the URL is one this component just minted from bytes
          // it fetched itself — not user input and not a server-supplied link.
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
        },
        error: (error) => {
          this.isPdfLoading = false;
          this.pdfFailure = classifyCureSheetError(error);
          this.pdfUrl = null;
        },
      });
  }

  /**
   * Revoke the object URL.
   *
   * Not housekeeping: these blobs are up to 20 MB and are held for the lifetime
   * of the document unless revoked, so browsing a library would accumulate every
   * PDF opened.
   */
  private releaseObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.pdfUrl = null;
  }
}
